<script lang="ts" setup>
import { i18n } from '@/i18n';
import { DISPLAY_ORDER_LIST_MENU_ITEMS } from '../constants';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { DisplayOrderListMenuItems } from '../entities/displayOrderListMenu';
import { DisplayOrderListService } from '../service/displayOrderListService';
import { watch } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';

const props = defineProps<{
  isOpenProp?: boolean;
  selectedItemProp?: DisplayOrderListMenuItems | undefined;
}>();

const t = i18n.t;

const selectedItem = ref<DisplayOrderListMenuItems>();
const isOpen = ref(false);

const OPTION_HEIGHT = 40;

const emit = defineEmits<{
  (e: 'click:selectInput'): void;
  (e: 'selectedItem', item: DisplayOrderListMenuItems): void;
  (e: 'close'): void;
}>();

watch(
  () => props.isOpenProp,
  () => {
    isOpen.value = props.isOpenProp || false;
  }
);

watch(
  () => props.selectedItemProp,
  () => {
    selectedItem.value = props.selectedItemProp;
  }
);

watch(isOpen, async (newVal) => {
  if (newVal) {
    emit('click:selectInput');
    calculateDropdownPosition();
  }
});

/**
 * ドロップダウンの表示状態を切り替えます。
 */
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

/**
 * オプションを選択し、親コンポーネントに選択値を通知します。
 * @param item - 選択したアイテム
 */
const selectOption = (item: DisplayOrderListMenuItems) => {
  selectedItem.value = item;
  emit('selectedItem', item);
  isOpen.value = false;
  emit('close');
};

/**
 * ドロップダウンを閉じます。
 * @param event - クリックイベント
 */
const closeDropdown = (event: MouseEvent) => {
  if (!isOpen.value) return;
  const target = event.target;
  const selector = '#display-order-menu-dropdown-container';
  const arrowSelector = '#display-order-menu-dropdown-arrow';
  const searchSelector = '#display-order-menu-dropdown-content';
  if (!(target instanceof Element)) return;
  if (target.closest(arrowSelector)) return;
  if (target.closest(selector)) return;
  if (target.closest(searchSelector)) return;

  isOpen.value = false;
  emit('close');
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.addEventListener('click', closeDropdown);
});

/**
 * ドロップダウンの位置を計算し、設定します。
 */
const calculateDropdownPosition = () => {
  nextTick(() => {
    const selectBox = document.querySelector('#display-order-menu-dropdown-container') as HTMLElement;
    if (selectBox) {
      const rect = selectBox.getBoundingClientRect();
      const dropdown = document.querySelector('#display-order-menu-dropdown-content') as HTMLElement;
      if (dropdown) {
        dropdown.style.left = `${rect.left}px`;
        dropdown.style.top = `${rect.bottom}px`;

        // ドロップダウンの高さを計算
        const desiredHeight = OPTION_HEIGHT * DISPLAY_ORDER_LIST_MENU_ITEMS.length;

        // max-heightを計算して設定
        const availableHeight = window.innerHeight - rect.bottom - 10; // 10pxは少しのマージン
        dropdown.style.maxHeight = `${Math.min(desiredHeight, availableHeight)}px`;
      }
    }
  });
};
</script>

<template>
  <div class="display-order-menu_container" @click="closeDropdown($event)">
    <div id="display-order-menu-dropdown-container" class="display-order-menu">
      <div class="display-order-menu__selected" @click="toggleDropdown">
        <div :class="`display-order-menu__field ${selectedItem === undefined ? 'display-order-menu__field--placeholder' : ''}`">
          <!-- 選択肢した値を表示する。何も選択していない場合はデフォルトの文言を表示する -->
          {{ t('発注情報の一括確認') }}
        </div>
        <SvgLoader v-if="isOpen" name="arrowUpBlue" class="display-order-menu__arrow" />
        <SvgLoader v-else id="display-order-menu-dropdown-arrow" name="arrowDownBlue" class="display-order-menu__arrow" />
      </div>
      <!-- TODO: 横スクロールした際にドロップダウンが追従しない -->
      <Teleport v-if="isOpen" to="body">
        <div id="display-order-menu-dropdown-content" class="display-order-menu__dropdown">
          <div
            v-for="option in DISPLAY_ORDER_LIST_MENU_ITEMS"
            :key="option"
            class="display-order-menu__option"
            @click="selectOption(option)"
          >
            {{ DisplayOrderListService.getDisplayText(option) }}
          </div>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<style scoped lang="scss">
.display-order-menu {
  justify-items: center;
  position: relative;
  user-select: none;
  box-sizing: border-box;
  font-family: Noto Sans JP;

  &__container {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 0;
  }

  &__field {
    height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0em;
    color: $semanticPrimary;
    background-color: $semanticWhite;
  }

  &__selected {
    width: 100%;
    height: 30px;
    padding: 2px 15px;
    display: grid;
    grid-template-columns: 1fr 24px;
    text-align: center;
    align-items: center;
    cursor: pointer;
    background-color: $semanticWhite;
    border: 1px solid $semanticPrimary;
    border-radius: 4px;
  }

  &__arrow {
    display: grid;
    align-content: center;
    justify-content: center;
    height: 24px;
    width: 24px;
  }

  &__dropdown {
    position: fixed;
    background-color: $semanticWhite;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1050; // モーダルより高い値に設定
    overflow-y: auto;
    max-height: 300px; // 適切な最大高さを設定
    box-sizing: border-box;
  }

  &__option {
    padding: 10px;
    cursor: pointer;

    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;

    min-height: 32px;

    &:hover,
    &:focus {
      background-color: $semanticPrimarySubdued;
    }

    &--disabled {
      color: #ccc;
      cursor: not-allowed;
    }
  }
}
</style>
